import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  EditorDraft,
  ExternalValidationResult,
  Rule,
  ValidationResult,
  ValidationRule,
} from '../interface/validation.interface';

@Injectable({
  providedIn: 'root',
})
export class ValidationState {
  /** state to store the  info when selecting rule from the validation list */
  private _selectedRuleSub$ = new BehaviorSubject<ValidationResult | null>(null);
  selectedRule$: Observable<ValidationResult | null> = this._selectedRuleSub$.asObservable();

  get selectedRule(): ValidationResult | null {
    return this._selectedRuleSub$.value;
  }

  set selectedRule(data: ValidationResult | null) {
    this._selectedRuleSub$.next(data);
  }

  /** state to store all front end validation rules from the api */
  private _validationRulesSub$ = new BehaviorSubject<Array<Rule> | null>(null);
  validationRules$: Observable<Array<Rule> | null> = this._validationRulesSub$.asObservable();

  get validationRules(): Array<Rule> | null {
    return this._validationRulesSub$.value;
  }

  set validationRules(data: Array<Rule> | null) {
    this._validationRulesSub$.next(data);
  }

  /** state to store all modified front end validation rules from the api */
  private _validationModifiedRulesSub$ = new BehaviorSubject<Array<ValidationRule> | null>(null);
  validationModifiedRules$: Observable<Array<ValidationRule> | null> = this._validationModifiedRulesSub$.asObservable();

  get validationModifiedRules(): Array<ValidationRule> | null {
    return this._validationModifiedRulesSub$.value;
  }

  set validationModifiedRules(data: Array<ValidationRule> | null) {
    this._validationModifiedRulesSub$.next(data);
  }

  /** state to store last audit time */
  private _validationAuditTimeSub$ = new BehaviorSubject<Date | null>(null);
  validationAuditTime$: Observable<Date | null> = this._validationAuditTimeSub$.asObservable();

  get validationAuditTime(): Date | null {
    return this._validationAuditTimeSub$.value;
  }

  set validationAuditTime(data: Date | null) {
    this._validationAuditTimeSub$.next(data);
  }

  /** state to track if validation audit is in progress */
  private _isValidationInProgressSub$ = new BehaviorSubject<boolean>(false);
  isValidationInProgress$: Observable<boolean> = this._isValidationInProgressSub$.asObservable();

  get isValidationInProgress(): boolean {
    return this._isValidationInProgressSub$.value;
  }

  set isValidationInProgress(data: boolean) {
    this._isValidationInProgressSub$.next(data);
  }

  /** state to store validation-sheet-toggle value */
  private _validationSheetToggleSub$ = new BehaviorSubject<boolean>(false);
  validationSheetToggle$: Observable<boolean> = this._validationSheetToggleSub$.asObservable();

  get validationSheetToggle(): boolean {
    return this._validationSheetToggleSub$.value;
  }

  set validationSheetToggle(data: boolean) {
    this._validationSheetToggleSub$.next(data);
  }

  /** Open editor's draft values for the live validation preview; never persisted. Cleared on close. */
  private _editorDraftSub$ = new BehaviorSubject<EditorDraft | null>(null);
  editorDraft$: Observable<EditorDraft | null> = this._editorDraftSub$.asObservable();

  get editorDraft(): EditorDraft | null {
    return this._editorDraftSub$.value;
  }

  set editorDraft(data: EditorDraft | null) {
    this._editorDraftSub$.next(data);
  }

  /** Stores external validation errors with no page/scope mapping (shown under "Other" section) */
  unmatchedExternalValidations: ValidationResult[] = [];

  /** Caches the last external validation results so validation can be re-run after route config resets */
  lastExternalValidationResults: ExternalValidationResult[] = [];

  /** Tracks the total validation rule count for badge display */
  validationCount: number = 0;

  /** Tracks active tab index in the slider (0 = Validation, 1 = Feedback) */
  private _activeSliderTabSub$ = new BehaviorSubject<number>(0);
  activeSliderTab$: Observable<number> = this._activeSliderTabSub$.asObservable();

  get activeSliderTab(): number {
    return this._activeSliderTabSub$.value;
  }

  set activeSliderTab(data: number) {
    this._activeSliderTabSub$.next(data);
  }
}
